
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent 
} from '@/components/ui/chart';
import { 
  ScatterChart, 
  Scatter, 
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Cell
} from 'recharts';
import { AdminSubmission } from '@/hooks/useAdminData';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Lightbulb,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Users,
  Zap,
  Building2
} from 'lucide-react';

interface BusinessInsightsProps {
  submissions: AdminSubmission[];
}

const BusinessInsights = ({ submissions }: BusinessInsightsProps) => {
  // Sector Performance Analysis - Fixed calculations
  const sectorPerformance = React.useMemo(() => {
    if (submissions.length === 0) return [];

    const sectors: Record<string, {
      count: number;
      totalScore: number;
      totalSavings: number;
      totalROI: number;
    }> = {};

    submissions.forEach(sub => {
      if (!sectors[sub.business_type]) {
        sectors[sub.business_type] = {
          count: 0,
          totalScore: 0,
          totalSavings: 0,
          totalROI: 0
        };
      }

      const sector = sectors[sub.business_type];
      sector.count++;
      sector.totalScore += sub.energy_potential_score || 0;
      sector.totalSavings += sub.yearly_annual_savings || 0;
      sector.totalROI += sub.roi_months || 0;
    });

    return Object.entries(sectors)
      .map(([type, data]) => ({
        sector: type,
        count: data.count,
        avgScore: data.count > 0 ? Math.round(data.totalScore / data.count) : 0,
        avgSavings: data.count > 0 ? Math.round(data.totalSavings / data.count) : 0,
        avgROI: data.count > 0 ? Math.round(data.totalROI / data.count) : 0,
        totalSavings: data.totalSavings,
        marketShare: Math.round((data.count / submissions.length) * 100)
      }))
      .filter(sector => sector.count > 0)
      .sort((a, b) => b.avgScore - a.avgScore);
  }, [submissions]);

  // Predictive Trend Analysis - Fixed calculations
  const trendData = React.useMemo(() => {
    if (submissions.length === 0) {
      // Return empty state with placeholder data
      return Array.from({ length: 9 }, (_, i) => ({
        month: new Date(2024, i, 1).toLocaleDateString('nl-NL', { month: 'short' }),
        submissions: 0,
        avgScore: 0,
        trend: i < 6 ? 'historical' : 'predicted'
      }));
    }

    const monthlyData = [];
    const now = new Date();
    
    // Historical data (last 6 months)
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('nl-NL', { month: 'short' });
      
      const monthSubmissions = submissions.filter(sub => {
        const subDate = new Date(sub.created_at);
        return subDate.getMonth() === date.getMonth() && 
               subDate.getFullYear() === date.getFullYear();
      });

      const avgScore = monthSubmissions.length > 0 
        ? Math.round(monthSubmissions.reduce((sum, sub) => sum + (sub.energy_potential_score || 0), 0) / monthSubmissions.length)
        : 0;

      monthlyData.push({
        month: monthName,
        submissions: monthSubmissions.length,
        avgScore: Math.max(0, avgScore),
        trend: 'historical'
      });
    }

    // Calculate growth rate safely
    const validMonths = monthlyData.filter(m => m.submissions > 0);
    let growthRate = 0.1; // Default 10% growth
    
    if (validMonths.length >= 2) {
      const recent = validMonths.slice(-2);
      if (recent[0].submissions > 0) {
        growthRate = Math.max(-0.5, Math.min(0.5, (recent[1].submissions - recent[0].submissions) / recent[0].submissions));
      }
    }

    const lastValidMonth = validMonths[validMonths.length - 1] || { submissions: 1, avgScore: 50 };

    // Predicted data (next 3 months)
    for (let i = 1; i <= 3; i++) {
      const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthName = futureDate.toLocaleDateString('nl-NL', { month: 'short' });
      
      const predictedSubmissions = Math.max(1, Math.round(lastValidMonth.submissions * Math.pow(1 + growthRate, i)));
      const predictedScore = Math.min(100, Math.max(0, lastValidMonth.avgScore + (Math.random() * 10 - 5)));
      
      monthlyData.push({
        month: monthName,
        submissions: predictedSubmissions,
        avgScore: Math.round(predictedScore),
        trend: 'predicted'
      });
    }

    return monthlyData;
  }, [submissions]);

  // Strategic Recommendations - Enhanced logic
  const recommendations = React.useMemo(() => {
    const recs = [];
    
    if (sectorPerformance.length === 0) {
      recs.push({
        type: 'warning',
        title: 'Onvoldoende data',
        description: 'Meer submissions nodig voor betrouwbare analyses en aanbevelingen.',
        impact: 'High',
        icon: AlertTriangle
      });
      return recs;
    }
    
    // High-value sectors
    const topSector = sectorPerformance[0];
    if (topSector && topSector.avgScore > 0) {
      recs.push({
        type: 'opportunity',
        title: `Focus op ${topSector.sector}`,
        description: `Hoogste gemiddelde score (${topSector.avgScore}%) met ${topSector.count} submissions. Verhoog marketing inspanningen.`,
        impact: 'High',
        icon: Target
      });
    }

    // Low conversion sectors
    const lowPerformers = sectorPerformance.filter(s => s.avgScore < 50 && s.count >= 2);
    if (lowPerformers.length > 0) {
      recs.push({
        type: 'warning',
        title: 'Verbeter conversie',
        description: `${lowPerformers.length} sectoren presteren onder verwachting. Overweeg aangepaste aanpak.`,
        impact: 'Medium',
        icon: AlertTriangle
      });
    }

    // Growth opportunity
    const totalSavings = submissions.reduce((sum, sub) => sum + (sub.yearly_annual_savings || 0), 0);
    if (totalSavings > 100000) {
      recs.push({
        type: 'success',
        title: 'Schaalvoordelen',
        description: `€${Math.round(totalSavings/1000)}K+ totale besparingen. Ontwikkel enterprise packages.`,
        impact: 'High',
        icon: DollarSign
      });
    }

    return recs;
  }, [submissions, sectorPerformance]);

  // Performance correlation data - Fixed structure
  const correlationData = React.useMemo(() => {
    return submissions
      .filter(sub => 
        sub.energy_potential_score && 
        sub.yearly_annual_savings && 
        sub.monthly_energy_costs &&
        sub.energy_potential_score > 0 &&
        sub.yearly_annual_savings > 0 &&
        sub.monthly_energy_costs > 0
      )
      .map((sub, index) => ({
        x: sub.monthly_energy_costs,
        y: Math.round(sub.yearly_annual_savings / 1000), // Convert to thousands
        z: sub.energy_potential_score, // For bubble size
        name: sub.business_type,
        id: index
      }));
  }, [submissions]);

  const chartConfig = {
    performance: { label: "Performance Analysis" },
    correlation: { label: "Cost vs Savings Correlation" },
    trends: { label: "Trend Analysis" }
  };

  const COLORS = ['#2D5530', '#00B4DB', '#FF7F00', '#4ADE80', '#38BDF8', '#FB923C'];

  // Show loading state for insufficient data
  if (submissions.length < 3) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Onvoldoende Data voor Insights
            </CardTitle>
            <CardDescription>
              Er zijn minimaal 3 submissions nodig voor betrouwbare business insights.
              Momenteel: {submissions.length} submissions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <Lightbulb className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Kom terug wanneer er meer data beschikbaar is voor analyse.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Strategic Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {recommendations.map((rec, index) => {
          const IconComponent = rec.icon;
          return (
            <Card key={index} className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-base">{rec.title}</CardTitle>
                  <Badge 
                    variant={rec.impact === 'High' ? 'default' : rec.impact === 'Medium' ? 'secondary' : 'outline'}
                    className="ml-auto"
                  >
                    {rec.impact}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">{rec.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Performance Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sector Performance Matrix */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Sector Performance Matrix
            </CardTitle>
            <CardDescription>Vergelijking van prestaties per bedrijfstype</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {sectorPerformance.slice(0, 5).map((sector, index) => (
                <div key={sector.sector} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-medium text-sm">{sector.sector}</span>
                      <Badge variant="outline" className="text-xs">
                        {sector.count} submissions
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-600">Score: {sector.avgScore}%</span>
                      <span className="text-green-600">€{Math.round(sector.avgSavings/1000)}K</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Progress value={Math.min(100, Math.max(0, sector.avgScore))} className="flex-1 h-2" />
                    <Progress value={Math.min(100, Math.max(0, sector.marketShare))} className="flex-1 h-2" />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Energy Score</span>
                    <span>Market Share ({sector.marketShare}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Predictive Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Predictive Trends
            </CardTitle>
            <CardDescription>Historische data en voorspellingen</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <LineChart data={trendData}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Line 
                  type="monotone" 
                  dataKey="submissions" 
                  stroke="#2D5530" 
                  strokeWidth={2}
                  dot={(props) => {
                    const { payload } = props;
                    return (
                      <circle
                        {...props}
                        fill={payload.trend === 'predicted' ? '#FF7F00' : '#2D5530'}
                        stroke={payload.trend === 'predicted' ? '#FF7F00' : '#2D5530'}
                        strokeWidth={2}
                        r={3}
                        strokeDasharray={payload.trend === 'predicted' ? '3,3' : '0'}
                      />
                    );
                  }}
                />
                <ChartTooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border rounded shadow-lg">
                          <p className="font-semibold">{label}</p>
                          <p className="text-sm">Submissions: {data.submissions}</p>
                          <p className="text-sm">Gemiddelde Score: {data.avgScore}%</p>
                          <p className="text-xs text-gray-500 capitalize">{data.trend}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </LineChart>
            </ChartContainer>
            <div className="flex justify-center gap-4 mt-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-0.5 bg-green-600"></div>
                <span>Historisch</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-0.5 bg-orange-500"></div>
                <span>Voorspelling</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost vs Savings Correlation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Cost vs Savings Correlation
            </CardTitle>
            <CardDescription>Relatie tussen energiekosten en besparingspotentieel</CardDescription>
          </CardHeader>
          <CardContent>
            {correlationData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <ScatterChart data={correlationData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <XAxis 
                    type="number"
                    dataKey="x"
                    name="Maandelijkse Kosten"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `€${value}`}
                  />
                  <YAxis 
                    type="number"
                    dataKey="y"
                    name="Jaarlijkse Besparingen"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `€${value}K`}
                  />
                  <Scatter dataKey="z" fill="#2D5530">
                    {correlationData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Scatter>
                  <ChartTooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 border rounded shadow-lg">
                            <p className="font-semibold">{data.name}</p>
                            <p className="text-sm">Maandkosten: €{data.x}</p>
                            <p className="text-sm">Jaarbesparingen: €{data.y}K</p>
                            <p className="text-sm">Energy Score: {data.z}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </ScatterChart>
              </ChartContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Onvoldoende data voor correlatie-analyse</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Business Intelligence Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Business Intelligence Metrics
            </CardTitle>
            <CardDescription>Belangrijke prestatie-indicatoren</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-lg font-bold text-green-700">
                  {submissions.length > 0 ? Math.round(submissions.reduce((sum, sub) => sum + (sub.energy_potential_score || 0), 0) / submissions.length) : 0}%
                </div>
                <div className="text-xs text-green-600">Gem. Energy Score</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-lg font-bold text-blue-700">
                  €{Math.round(submissions.reduce((sum, sub) => sum + (sub.yearly_annual_savings || 0), 0) / 1000)}K
                </div>
                <div className="text-xs text-blue-600">Totale Besparingen</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-lg font-bold text-orange-700">
                  {submissions.length > 0 ? Math.round(submissions.reduce((sum, sub) => sum + (sub.roi_months || 0), 0) / submissions.length) : 0}
                </div>
                <div className="text-xs text-orange-600">Gem. ROI (maanden)</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-700">
                  {submissions.length > 0 ? Math.round((submissions.filter(s => s.expansion_plans.length > 0).length / submissions.length) * 100) : 0}%
                </div>
                <div className="text-xs text-purple-600">Uitbreidingsplannen</div>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <h4 className="font-semibold text-sm mb-3">Market Opportunities</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span>High-value prospects ({'>'}80% score)</span>
                  <Badge variant="default">
                    {submissions.filter(s => (s.energy_potential_score || 0) > 80).length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span>Quick wins (ROI {'<'} 12 mnd)</span>
                  <Badge variant="secondary">
                    {submissions.filter(s => (s.roi_months || 0) < 12).length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span>Enterprise potential ({'>'}50 werknemers)</span>
                  <Badge variant="outline">
                    {submissions.filter(s => parseInt(s.employee_count) > 50).length}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessInsights;
